import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Pencil, Trash2, FileText } from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Date' },
];

export default function DealCustomFields({ customFields = [], onChange }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newField, setNewField] = useState({ label: '', value: '', type: 'text' });

  const handleAddField = () => {
    if (!newField.label.trim()) return;
    
    const field = {
      id: `cf_${Date.now()}`,
      label: newField.label.trim(),
      value: newField.value,
      type: newField.type
    };
    
    onChange([...customFields, field]);
    setNewField({ label: '', value: '', type: 'text' });
    setShowAddDialog(false);
  };

  const handleUpdateField = () => {
    if (editingIndex === null || !newField.label.trim()) return;
    
    const updated = [...customFields];
    updated[editingIndex] = {
      ...updated[editingIndex],
      label: newField.label.trim(),
      value: newField.value,
      type: newField.type
    };
    
    onChange(updated);
    setNewField({ label: '', value: '', type: 'text' });
    setEditingIndex(null);
  };

  const handleDeleteField = (index) => {
    const updated = customFields.filter((_, i) => i !== index);
    onChange(updated);
  };

  const openEditDialog = (index) => {
    const field = customFields[index];
    setNewField({ label: field.label, value: field.value, type: field.type });
    setEditingIndex(index);
  };

  const renderFieldInput = (field, index) => {
    const updateValue = (value) => {
      const updated = [...customFields];
      updated[index] = { ...updated[index], value };
      onChange(updated);
    };

    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            value={field.value || ''}
            onChange={(e) => updateValue(e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            className="border-[#E2E8F0] min-h-[80px]"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={field.value || ''}
            onChange={(e) => updateValue(e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            className="border-[#E2E8F0]"
          />
        );
      case 'url':
        return (
          <Input
            type="url"
            value={field.value || ''}
            onChange={(e) => updateValue(e.target.value)}
            placeholder="https://..."
            className="border-[#E2E8F0]"
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={field.value || ''}
            onChange={(e) => updateValue(e.target.value)}
            className="border-[#E2E8F0]"
          />
        );
      default:
        return (
          <Input
            type="text"
            value={field.value || ''}
            onChange={(e) => updateValue(e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            className="border-[#E2E8F0]"
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-brand" />
          <h3 className="text-md font-semibold text-brand">Deal-Specific Fields</h3>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setNewField({ label: '', value: '', type: 'text' });
            setShowAddDialog(true);
          }}
          className="border-brand text-brand hover:bg-brand hover:text-white"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Field
        </Button>
      </div>

      {customFields.length === 0 ? (
        <p className="text-sm text-[#64748B] italic">
          No custom fields added. Add fields specific to this deal only.
        </p>
      ) : (
        <Card className="border-[#E2E8F0]">
          <CardContent className="pt-4 space-y-4">
            {customFields.map((field, index) => (
              <div key={field.id || index} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-[#0F172A]">{field.label}</Label>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(index)}
                      className="h-7 w-7 p-0 text-[#64748B] hover:text-brand"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteField(index)}
                      className="h-7 w-7 p-0 text-[#64748B] hover:text-red-500"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {renderFieldInput(field, index)}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Add Field Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-[#0F172A]">
              Add Custom Field
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Field Label *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                placeholder="e.g., Special Terms"
                className="border-[#E2E8F0]"
              />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v })}>
                <SelectTrigger className="border-[#E2E8F0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Initial Value (Optional)</Label>
              {newField.type === 'textarea' ? (
                <Textarea
                  value={newField.value}
                  onChange={(e) => setNewField({ ...newField, value: e.target.value })}
                  placeholder="Enter value..."
                  className="border-[#E2E8F0]"
                />
              ) : (
                <Input
                  type={newField.type === 'number' ? 'number' : newField.type === 'date' ? 'date' : 'text'}
                  value={newField.value}
                  onChange={(e) => setNewField({ ...newField, value: e.target.value })}
                  placeholder="Enter value..."
                  className="border-[#E2E8F0]"
                />
              )}
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddField}
              disabled={!newField.label.trim()}
              className="!bg-brand hover:!bg-brand-hover !text-white"
            >
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-[#0F172A]">
              Edit Custom Field
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Field Label *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                placeholder="e.g., Special Terms"
                className="border-[#E2E8F0]"
              />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select value={newField.type} onValueChange={(v) => setNewField({ ...newField, type: v })}>
                <SelectTrigger className="border-[#E2E8F0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditingIndex(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateField}
              disabled={!newField.label.trim()}
              className="!bg-brand hover:!bg-brand-hover !text-white"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
